/**
 * Purpose: Manages web push notification subscription state and operations
 *
 * How it works:
 * 1. Tracks user subscription preferences in local storage
 * 2. Connects to socket and automatically restores subscriptions when connected
 * 3. Provides utilities for requesting permissions, generating and deleting subscriptions
 * 4. Offers a simple toggle function for enabling/disabling push notifications
 * 5. Handles timeouts when waiting for server public key
 *
 * Key features:
 * - Browser support detection for web push
 * - User-specific storage keys based on username
 * - Notification permission request handling
 * - Subscription generation with server public key
 * - Subscription deletion and restoration
 * - Error handling with translated error messages
 * - Timeout management for public key retrieval
 */

import { useCallback, useEffect, useMemo } from "react";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { useSocket } from "@/socketIO/useSocket";
import useAuthSession from "@/middleware/hooks/useAuthSession";
import { useTranslation } from "react-i18next";

// Import utilities
import {
  PUBLIC_KEY_TIMEOUT_MS,
  isWebPushSupported,
  getWebPushStorageKeys,
  generateWebPushSubscription,
  deleteWebPushSubscription,
  waitForWebPushPublicKey,
  getWebPushTranslatedMessage,
  isWebPushError,
  restoreWebPushSubscriptionIfNeeded,
} from "./webPushUtils";

export function useWebPush() {
  const socket = useSocket();
  const { username } = useAuthSession();
  const { t } = useTranslation();

  // Determine if web push is supported by the browser
  const isSupported = useMemo(() => isWebPushSupported(), []);

  // Get the storage keys for this user
  const storageKeys = useMemo(
    () =>
      username
        ? getWebPushStorageKeys(username)
        : {
            userPreference: "",
            subscriptionData: "",
            activeStatus: "",
          },
    [username],
  );

  // Track user subscription preference
  const [isSubscribed, setIsSubscribed] = useLocalStorage(
    storageKeys.userPreference,
    false,
  );

  useEffect(() => {
    if (!socket || !username) {
      return;
    }
    const handleConnect = () => {
      // Now the socket is guaranteed to be connected
      restoreWebPushSubscriptionIfNeeded(socket, username).catch((error) => {
        // eslint-disable-next-line no-console
        console.error("Failed to restore web push subscription:", error);
      });
    };

    socket.on("connect", handleConnect);

    // If already connected, run immediately
    if (socket.connected) {
      handleConnect();
    }

    return () => {
      socket.off("connect", handleConnect);
    };
  }, [socket, username]);

  // Generate subscription with server public key
  const generateSubscription = useCallback(
    async (key: string) => {
      if (!socket || !username) {
        throw new Error(t("webPush.errors.socketNotConnected"));
      }

      try {
        // Use the generateWebPushSubscription function
        const sub = await generateWebPushSubscription(key, socket, username);
        setIsSubscribed(true);
        return sub;
      } catch (error: unknown) {
        if (isWebPushError(error)) {
          throw new Error(getWebPushTranslatedMessage(error));
        } else {
          throw new Error(t("webPush.errors.unknownError"));
        }
      }
    },
    [socket, username, setIsSubscribed, t],
  );

  // Delete subscription
  const deleteSubscription = useCallback(async () => {
    if (!socket || !username) {
      throw new Error(t("webPush.errors.socketNotConnected"));
    }

    try {
      // Use the deleteWebPushSubscription function
      await deleteWebPushSubscription(socket, username);
      setIsSubscribed(false);
    } catch (error: unknown) {
      // The service already throws translated errors
      if (isWebPushError(error)) {
        throw new Error(getWebPushTranslatedMessage(error));
      }
      throw error;
    }
  }, [socket, username, setIsSubscribed, t]);

  // Wait for public key with timeout
  const waitForPublicKey = useCallback(
    (timeoutMs: number = PUBLIC_KEY_TIMEOUT_MS): Promise<string> => {
      if (!socket) {
        throw new Error(t("webPush.errors.socketNotConnected"));
      }

      try {
        // Use the waitForWebPushPublicKey function
        return waitForWebPushPublicKey(socket, timeoutMs);
      } catch (error: unknown) {
        // The service already throws translated errors
        if (isWebPushError(error)) {
          throw new Error(getWebPushTranslatedMessage(error));
        }
        throw error;
      }
    },
    [socket, t],
  );

  // Request notification permission
  const requestPermission =
    useCallback(async (): Promise<NotificationPermission> => {
      try {
        return await Notification.requestPermission();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to request notification permission:", error);
        throw new Error(t("webPush.errors.notificationPermissionDenied"));
      }
    }, [t]);

  // Toggle subscription
  const toggleSubscription = useCallback(async () => {
    // Check if web push is supported
    if (!isSupported) {
      throw new Error(
        t("webPush.errors.notSupported", {
          defaultValue: "Web Push not supported in this browser",
        }),
      );
    }

    if (!isSubscribed) {
      const permission = await requestPermission();
      if (permission === "granted") {
        // Wait for public key before generating subscription
        const key = await waitForPublicKey(PUBLIC_KEY_TIMEOUT_MS);
        await generateSubscription(key);
      } else {
        throw new Error(t("webPush.errors.notificationPermissionDenied"));
      }
    } else {
      await deleteSubscription();
    }
  }, [
    isSupported,
    isSubscribed,
    requestPermission,
    waitForPublicKey,
    generateSubscription,
    deleteSubscription,
    t,
  ]);

  return {
    isSubscribed, // User preference (did they toggle on/off)
    toggleSubscription,
    waitForPublicKey,
    generateSubscription,
  };
}
